import { useNavigate } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { RULE_ACTION_LABELS, RULE_ACTIONS, RULE_EFFECT_LABELS, RULE_EFFECTS, type RuleAction, type RuleEffect, type RuleView } from "../../shared/rules";
import { api, errorText } from "../lib/api";
import { useSettings } from "../lib/agent";
import { handOffToChat } from "../lib/chat-handoff";
import { agentPronoun, capitalize } from "../lib/format";
import { mutateResource, useResource } from "../lib/store";
import { IconX } from "./icons";
import { Button, cx, Field, Input, Loading, Notice, Select, Spinner } from "./ui";

const ORIGIN_LABEL: Record<RuleView["createdFrom"], string> = {
  message: "dite à ton agent",
  approval: "« Toujours autoriser »",
  app: "ajoutée ici",
};

export function useRules() {
  return useResource<RuleView[]>("rules", async (signal) => (await api("GET /api/rules", { signal })).rules, { refreshOn: ["rules.changed"] });
}

interface Draft {
  effect: RuleEffect;
  action: RuleAction;
  app: string;
  address: string;
  keyword: string;
}

const EMPTY: Draft = { effect: "allow", action: "reply_email", app: "", address: "", keyword: "" };

/**
 * « Tes règles » (the aside of Validations, data-testid="rules-panel"): what the agent does without asking, what it
 * always asks, what it never does — each with a delete button; a new rule in one sentence (handed to the agent), or
 * a precise one through the small form.
 */
export function RulesPanel() {
  const rules = useRules();
  const settings = useSettings();
  const navigate = useNavigate();
  const agentName = settings.data?.agentName ?? "Ton agent";
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [sentence, setSentence] = useState("");
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = useId();
  const id = (name: string) => `${p}-${name}`;

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setError(null);
  }

  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const rule = await api("POST /api/rules", {
        body: {
          effect: draft.effect,
          action: draft.action,
          ...(draft.app.trim() ? { app: draft.app.trim() } : {}),
          ...(draft.address.trim() ? { address: draft.address.trim() } : {}),
          ...(draft.keyword.trim() ? { keyword: draft.keyword.trim() } : {}),
        },
      });
      mutateResource<RuleView[]>("rules", (list) => [rule, ...(list ?? []).filter((r) => r.id !== rule.id && !(r.action === rule.action && r.app === rule.app && r.address === rule.address && r.contact === rule.contact && r.keyword === rule.keyword))]);
      setDraft(EMPTY);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule: RuleView) {
    if (removing) return;
    setRemoving(rule.id);
    setError(null);
    try {
      await api("DELETE /api/rules/:id", { params: { id: rule.id } });
      mutateResource<RuleView[]>("rules", (list) => list?.filter((r) => r.id !== rule.id));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRemoving(null);
    }
  }

  return (
    <aside id="regles" aria-labelledby={id("title")} className="flex min-w-0 scroll-mt-24 flex-col gap-2">
      <h2 id={id("title")} className="px-1 text-[13px] font-semibold text-subtle" title={`${agentName} agit par défaut. ${agentPronoun(settings.data, true)} ne demande que pour ce qui engage d'autres personnes ou ne se défait pas.`}>
        Tes règles
      </h2>
      <div data-testid="rules-panel" className="flex flex-col gap-2">
        {rules.loading && !rules.data ? (
          <Loading label="Chargement des règles…" />
        ) : rules.data?.length ? (
          <ul className="flex min-w-0 flex-col rounded-[20px] bg-elev px-4 py-1 [&>*+*]:border-t [&>*+*]:border-line">
            {rules.data.map((rule) => (
              <li key={rule.id} data-testid="rule-row" className="flex min-w-0 items-start gap-2.5 py-1">
                <span aria-hidden="true" className={cx("mt-[17px] h-[7px] w-[7px] shrink-0 rounded-full", rule.effect === "allow" ? "bg-success" : rule.effect === "ask" ? "bg-accent" : "bg-danger")} />
                <span className="min-w-0 flex-1 py-2.5 text-[15px] leading-[1.4] wrap-anywhere">
                  <span className="sr-only">{RULE_EFFECT_LABELS[rule.effect]} : </span>
                  {ruleSentence(rule)}
                  <span className="block text-[12.5px] text-subtle">{ORIGIN_LABEL[rule.createdFrom]}</span>
                </span>
                <button
                  type="button"
                  disabled={removing !== null}
                  aria-busy={removing === rule.id || undefined}
                  onClick={() => void remove(rule)}
                  aria-label={`Supprimer la règle : ${rule.label}`}
                  className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-subtle transition hover:bg-chip hover:text-fg disabled:opacity-50"
                >
                  {removing === rule.id ? <Spinner className="h-3.5 w-3.5" /> : <IconX width={15} height={15} />}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-1 text-[14px] text-muted">Aucune règle pour l'instant.</p>
        )}

        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!sentence.trim()) return;
            handOffToChat(`Nouvelle règle : ${sentence.trim()}`, true);
            void navigate({ to: "/discussion" });
          }}
        >
          <label htmlFor={id("sentence")} className="sr-only">
            Nouvelle règle, en une phrase
          </label>
          <input
            id={id("sentence")}
            value={sentence}
            maxLength={300}
            onChange={(e) => setSentence(e.target.value)}
            enterKeyHint="send"
            placeholder="Une nouvelle règle, en une phrase"
            className="h-12 min-w-0 rounded-full bg-elev px-5 text-[15px] text-fg shadow-[inset_0_0_0_1px_var(--border)] outline-none placeholder:text-subtle focus:shadow-[inset_0_0_0_1.5px_var(--fg-subtle)]"
          />
        </form>

        <details className="group">
          <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1 px-1 text-[13px] text-subtle select-none hover:text-fg [&::-webkit-details-marker]:hidden">
            <span>Règle précise</span>
            <span aria-hidden="true" className="text-[11px] transition group-open:rotate-90">
              ›
            </span>
          </summary>
          <form onSubmit={(e) => void add(e)} className="mt-2 space-y-3 rounded-[20px] bg-elev p-4" aria-label="Ajouter une règle">
            <Field label="Ton agent…" htmlFor={id("effect")}>
              <Select id={id("effect")} value={draft.effect} onChange={(e) => set("effect", e.target.value as RuleEffect)}>
                {RULE_EFFECTS.map((effect) => (
                  <option key={effect} value={effect}>
                    {RULE_EFFECT_LABELS[effect]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Action" htmlFor={id("action")}>
              <Select id={id("action")} value={draft.action} onChange={(e) => set("action", e.target.value as RuleAction)}>
                {RULE_ACTIONS.map((action) => (
                  <option key={action} value={action}>
                    {RULE_ACTION_LABELS[action]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Adresse ou domaine (facultatif)" htmlFor={id("address")}>
              <Input id={id("address")} value={draft.address} maxLength={200} inputMode="email" autoComplete="off" placeholder="marie@exemple.fr" onChange={(e) => set("address", e.target.value)} />
            </Field>
            <Field label="App (facultatif)" htmlFor={id("app")}>
              <Input id={id("app")} value={draft.app} maxLength={40} autoComplete="off" placeholder="gmail" onChange={(e) => set("app", e.target.value)} />
            </Field>
            <Field label="Mot-clé (facultatif)" htmlFor={id("keyword")}>
              <Input id={id("keyword")} value={draft.keyword} maxLength={80} autoComplete="off" onChange={(e) => set("keyword", e.target.value)} />
            </Field>
            <Button type="submit" size="sm" busy={busy}>
              Ajouter la règle
            </Button>
          </form>
        </details>

        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
      </div>
    </aside>
  );
}

/** A rule as one sentence (« Répondre aux mails de ma sœur sans me demander »). */
function ruleSentence(rule: RuleView): string {
  const scope = rule.label.slice(rule.label.indexOf(" : ") + 3);
  const what = capitalize(scope);
  if (rule.effect === "allow") return `${what} sans me demander`;
  if (rule.effect === "ask") return `Toujours me demander avant ${/^[aeiouyéèêh]/i.test(scope) ? "d'" : "de "}${scope}`;
  return `Ne jamais ${scope}`;
}
